import { Box, ButtonBase, Skeleton, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

export interface DiscrepancySummaryTile<K extends string> {
  key: K;
  label: string;
  count: number;
  /** Shows the same small red dot the section headers use for time-critical items. */
  hasUrgent?: boolean;
}

export interface DiscrepancySummaryBarProps<K extends string> {
  tiles: readonly DiscrepancySummaryTile<K>[];
  loading: boolean;
  /**
   * Tiles rendered in the "on" state. Empty means no filter is applied (all
   * sections showing) — the neutral resting state.
   */
  activeKeys: readonly K[];
  onTileClick: (key: K) => void;
}

/**
 * A horizontal scoreboard above the Discrepancy Review sections: one equal-width
 * tile per issue type showing its count. This is the only section-filter
 * control: with no tile lit, everything shows; clicking a tile from that
 * neutral state solos its section, further clicks toggle sections in and out
 * of the visible subset (chart-legend semantics). Deliberately a local,
 * lighter-weight cousin of the staff Manifest Review summary tiles — same
 * visual language, no icon legend.
 */
export function DiscrepancySummaryBar<K extends string>({
  tiles,
  loading,
  activeKeys,
  onTileClick,
}: DiscrepancySummaryBarProps<K>) {
  return (
    <Stack direction="row" spacing={1}>
      {tiles.map((tile) => {
        const active = activeKeys.includes(tile.key);
        return (
          // ButtonBase (a native <button>) keeps the tiles focusable and
          // Enter/Space-operable — they're the only section-filter control.
          <ButtonBase
            key={tile.key}
            aria-pressed={active}
            aria-label={`Filter by ${tile.label}`}
            onClick={() => onTileClick(tile.key)}
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 44,
              borderRadius: 1,
              px: 1.5,
              py: 1,
              border: 1,
              borderColor: active ? "primary.main" : "transparent",
              bgcolor: active ? (theme) => alpha(theme.palette.primary.main, 0.16) : "action.hover",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              textAlign: "left",
              transition: "background-color 0.15s ease",
              "&:hover": {
                bgcolor: (theme) => alpha(theme.palette.primary.main, active ? 0.22 : 0.12),
              },
              "&.Mui-focusVisible": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: 1,
              },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              {tile.hasUrgent && (
                <Box
                  data-testid={`summary-urgent-${tile.key}`}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "error.dark",
                    flexShrink: 0,
                  }}
                />
              )}
              <Typography
                variant="body2"
                color={active ? "primary.main" : "text.secondary"}
                sx={{ lineHeight: 1.25, minWidth: 0 }}
              >
                {tile.label}
              </Typography>
            </Stack>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500, flexShrink: 0 }}>
              {loading ? <Skeleton width={24} /> : tile.count}
            </Typography>
          </ButtonBase>
        );
      })}
    </Stack>
  );
}
